import moment from "moment";

/**
 * Get active closures from hotel data filtered by order type.
 * - "ONLINE_ORDER" or "ALL" closures apply to both PICKUP and DELIVERY
 * - "PICKUP" closures only apply when orderType is PICKUP
 * - "DELIVERY" closures only apply when orderType is DELIVERY
 */
export function getActiveClosures(hotel, orderType) {
  if (
    !hotel?.upcomming_scheduled_disable_data ||
    !Array.isArray(hotel.upcomming_scheduled_disable_data)
  ) {
    return [];
  }
  return hotel.upcomming_scheduled_disable_data.filter((c) => {
    if (c.domain_type === "ONLINE_ORDER" || c.domain_type === "ALL") {
      return true;
    }
    if (c.domain_type === "PICKUP" && orderType === "PICKUP") {
      return true;
    }
    if (c.domain_type === "DELIVERY" && orderType === "DELIVERY") {
      return true;
    }
    return false;
  });
}

/**
 * Check if a given epoch timestamp falls within any closure window
 */
export function isTimeInClosure(epochMs, closures) {
  if (!closures || !closures.length) return false;
  return closures.some(
    (c) => epochMs >= c.schedule_start_time && epochMs < c.schedule_end_time
  );
}

/**
 * Filter time slots by removing any that fall within a closure window.
 * Applied AFTER normal slot generation as a final filter.
 */
export function filterSlotsByClosures(
  slots,
  selectedDate,
  closures,
  bufferTime = 0
) {
  if (!closures.length || !slots.length) {
    return slots;
  }

  const dateStr = moment(selectedDate).format("YYYY-MM-DD");
  const bufferMs = (Number(bufferTime) || 0) * 60 * 1000;

  // Quick check: does any closure (extended by buffer) overlap with this
  // date at all?
  const dayStart = moment(dateStr, "YYYY-MM-DD").startOf("day").valueOf();
  const dayEnd = moment(dateStr, "YYYY-MM-DD").endOf("day").valueOf();
  const hasOverlap = closures.some(
    (c) =>
      c.schedule_start_time < dayEnd &&
      c.schedule_end_time + bufferMs > dayStart
  );
  if (!hasOverlap) {
    return slots;
  }

  const slotEpoch = (slot) =>
    moment(
      `${dateStr} ${slot.value}`,
      "YYYY-MM-DD hh:mm a"
    ).valueOf();

  // Effective closure window = [schedule_start_time, schedule_end_time +
  // bufferTime]. Inclusive on both ends so a slot landing exactly on a
  // closure end (e.g. 11:59 PM slot vs a 10:45–11:59 PM closure) is
  // dropped, and slots within the buffer-time after a closure ends are
  // also dropped — the hotel needs that prep time before the next order
  // can be served. `isTimeInClosure` keeps the un-buffered half-open
  // semantics it needs elsewhere.
  //
  // The ASAP entry is exempt from this filter. Its value is a display
  // hint of "now + buffer" (the estimated ready time), not a candidate
  // SCHEDULED slot the user is picking. ASAP availability is gated
  // upstream by `isAsapAvailable`, which already checks whether `now`
  // is inside a closure. If `now` is before the closure starts (e.g.
  // 7:50 PM with closure 8 PM–9 PM), the user should still be able to
  // place an ASAP order even though `now + buffer` falls inside the
  // closure — that's the kitchen's prep-time problem, not an order
  // cutoff. Dropping ASAP here was making `getLabel(slots)` return
  // undefined and disabling the radio even though `isAsapAvailable`
  // said true.
  const filtered = slots.filter((slot) => {
    if (slot.typeOfOrder === "ASAP") return true;
    const e = slotEpoch(slot);
    return !closures.some(
      (c) =>
        typeof c?.schedule_start_time === "number" &&
        typeof c?.schedule_end_time === "number" &&
        e >= c.schedule_start_time &&
        e <= c.schedule_end_time + bufferMs
    );
  });

  // Inject a wall-clock-aligned slot at roundUp(cEnd + buffer, 15-min) so
  // a closure ending at e.g. 7:28 PM + 30-min buffer surfaces "8:00 PM"
  // as the next slot instead of whatever the next running-hours-aligned
  // grid slot is (e.g. "8:10 PM" when the schedule starts at an offset
  // minute).
  //
  // The injection is only meaningful when `rounded` sits *inside* an
  // operational interval — i.e. there's a kept slot within one grid step
  // (15 min) after it. Otherwise `rounded` lands in a non-operational
  // gap and we'd surface a phantom slot for a time the shop is closed.
  // Examples this guard blocks:
  //   (a) Shop opens 09:00 NZ, closure ends 04:29 NZ → rounded 04:45.
  //       Next kept slot is 09:15 (4+ hours away) → skip.
  //   (b) Shop hours have a morning extension 00:00–03:00 + evening
  //       09:00–23:59 NZ, closure spans 17:18 → 04:18 NZ. After filter,
  //       the morning extension is fully dropped (in closure) and the
  //       next kept slot is 09:15 — still 4+ hours away from rounded
  //       04:45 → skip.
  // Whereas a mid-day closure (e.g. 7:28 PM, buf 30 → rounded 8:00 PM)
  // has its next kept slot at 8:00 or 8:15 PM → inject.
  const fifteenMs = 15 * 60 * 1000;
  closures.forEach((c) => {
    if (typeof c?.schedule_end_time !== "number") return;
    const postMs = c.schedule_end_time + bufferMs;
    if (postMs < dayStart || postMs >= dayEnd) return;
    const remainder = postMs % fifteenMs;
    const rounded =
      remainder === 0 ? postMs : postMs + (fifteenMs - remainder);
    const slotValue = moment(rounded).format("hh:mm a");
    if (filtered.some((s) => s.value === slotValue)) return;
    const hasContiguousOperationalSlot = filtered.some((s) => {
      const e = slotEpoch(s);
      return e > rounded && e - rounded <= fifteenMs;
    });
    if (!hasContiguousOperationalSlot) return;
    filtered.push({
      typeOfOrder: "SCHEDULED",
      value: slotValue,
      label: slotValue,
    });
  });

  // Sort by clock time so the injected slot lands in chronological order
  // and the dropdown's first entry after a closure is the rounded boundary.
  filtered.sort((a, b) => slotEpoch(a) - slotEpoch(b));

  return filtered;
}
